import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { 
  Users, 
  Save,
  RefreshCw,
  Shield,
  Check
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

import { API_BASE_URL } from '../utils/api';
const API_URL = API_BASE_URL;

const ROLES = [
  { id: 'customer_admin', name: 'Customer Admin', color: 'bg-blue-500' },
  { id: 'phone_admin', name: 'Phone Admin', color: 'bg-green-500' },
  { id: 'standard', name: 'Standard', color: 'bg-slate-500' }
];

export default function AdminPermissions() {
  const { admin } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [matrix, setMatrix] = useState({});
  const [features, setFeatures] = useState([]);

  useEffect(() => {
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/api/admin-portal/permissions-matrix`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMatrix(response.data.features);
      setFeatures(Object.values(response.data.features));
    } catch (error) {
      toast.error('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const toggleRole = async (featureId, roleId) => {
    const feature = matrix[featureId];
    const currentRoles = feature.roles || [];
    const newRoles = currentRoles.includes(roleId)
      ? currentRoles.filter(r => r !== roleId)
      : [...currentRoles, roleId];
    
    // Update both matrix and features list
    const updatedMatrix = {
      ...matrix,
      [featureId]: { ...matrix[featureId], roles: newRoles }
    };
    setMatrix(updatedMatrix);
    setFeatures(Object.values(updatedMatrix));
    
    // Save to backend
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API_URL}/api/admin-portal/permissions-matrix/${featureId}`, newRoles, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Permission updated');
    } catch (error) {
      toast.error('Failed to update permission');
      // Revert on error
      fetchPermissions();
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Permissions</h1>
          <p className="text-slate-400 mt-1">
            Configure role-based access to features
          </p>
        </div>
        <Button variant="outline" onClick={fetchPermissions} className="border-slate-600 text-slate-300 hover:bg-slate-700">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Role Legend */}
      <div className="flex items-center gap-4 mb-6">
        <span className="text-slate-400 text-sm">Roles:</span>
        {ROLES.map(role => (
          <Badge key={role.id} className={`${role.color}`}>
            {role.name}
          </Badge>
        ))}
      </div>

      {/* Permissions Matrix */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-red-400" />
            Feature Permissions Matrix
          </CardTitle>
          <CardDescription className="text-slate-400">
            Check which roles can access each feature. Changes are saved automatically.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {/* Header */}
            <div className="grid grid-cols-[1fr,repeat(3,100px)] gap-4 p-3 bg-slate-700/30 rounded-lg">
              <div className="text-sm font-medium text-slate-300">Feature</div>
              {ROLES.map(role => (
                <div key={role.id} className="text-sm font-medium text-slate-300 text-center">
                  {role.name}
                </div>
              ))}
            </div>
            
            {/* Feature rows */}
            {features.map(feature => (
              <div 
                key={feature.id} 
                className="grid grid-cols-[1fr,repeat(3,100px)] gap-4 p-3 rounded-lg hover:bg-slate-700/20 transition-colors"
              >
                <div>
                  <p className="font-medium text-white">{feature.name}</p>
                  <p className="text-xs text-slate-400">{feature.description}</p>
                </div>
                {ROLES.map(role => (
                  <div key={role.id} className="flex items-center justify-center">
                    <Checkbox
                      checked={feature.roles?.includes(role.id)}
                      onCheckedChange={() => toggleRole(feature.id, role.id)}
                      className="border-slate-500 data-[state=checked]:bg-red-500 data-[state=checked]:border-red-500"
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Info */}
      <Card className="bg-slate-800/50 border-slate-700 mt-6">
        <CardContent className="py-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h3 className="font-medium text-white mb-1">Role Hierarchy</h3>
              <p className="text-sm text-slate-400">
                <strong>Customer Admin</strong> - Full access to company features, user management<br />
                <strong>Phone Admin</strong> - Can manage phone settings and contacts<br />
                <strong>Standard</strong> - Basic user access to calling features
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
